import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Paper, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { JobTitleMap } from '../../../../../services/ticketEasyService/model/ticketEasy/jobTitleMapping/JobTitleMap';
import { useJobTitleMapDeleteMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';

interface ExistingMappingsViewProps {
    mappings: JobTitleMap[];
    isViewMode?: boolean;
    onDeleteSuccess?: () => void;
}

function ExistingMappingsView({ mappings, isViewMode = false, onDeleteSuccess }: ExistingMappingsViewProps) {
    const [deleteJobTitleMap] = useJobTitleMapDeleteMutation();

    // Filter only mappings that have a related title and sort alphabetically by PSN job title
    const validMappings = mappings
        .filter(mapping => mapping.relatedTitle?.trim().length > 0)
        .sort((a, b) => a.jobTitle.localeCompare(b.jobTitle));

    const handleDelete = async (mappingId: string) => {
        try {
            await deleteJobTitleMap(mappingId).unwrap();
            if (onDeleteSuccess) {
                onDeleteSuccess();
            }
        } catch (error) {
            console.error('Error deleting job title mapping:', error);
        }
    };
    
    if (!validMappings.length) {
        return (
            <Box mt={3}>
                <Typography variant="subtitle1">No mappings available.</Typography>
            </Box>
        );
    }    return (
        <Box mt={3} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Typography variant="h6" gutterBottom>
                Mappings
            </Typography>
            <Paper 
                elevation={2} 
                sx={{ 
                    flex: 1,
                    overflow: 'auto',
                    minHeight: '200px',
                    maxHeight: 'calc(100vh - 250px)'  // Dynamically calculate max height based on viewport
                }}
            >                <List dense>
                    {validMappings.map((mapping, index) => (
                        <React.Fragment key={`mapping-${mapping.jobTitle}-${index}`}>
                            <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                                <ListItemText
                                    primary={mapping.jobTitle}
                                    secondary={`Maps to: ${mapping.relatedTitle}`}
                                    primaryTypographyProps={{
                                        fontWeight: 'bold',
                                    }}
                                    secondaryTypographyProps={{
                                        color: 'primary',
                                    }}
                                    sx={{ flex: 1 }}
                                />
                                {isViewMode && (
                                    <Tooltip title="Delete mapping">
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => handleDelete(mapping.id)}
                                            size="small"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
        </Box>
    );
}

export { ExistingMappingsView };
